<template>
  <div class="data-table">
    <v-card elevation="0" min-width="100%">
      <v-card-title v-if="title || hasTableActions">
        <span class="table-title" v-if="title">{{ title }}</span>
        <v-spacer></v-spacer>
        <slot name="actions"></slot>
      </v-card-title>
      <v-data-table
        v-model="selectedItems"
        :hide-default-footer="isMobile"
        :hide-default-header="isMobile"
        :disable-sort="hasSearchText"
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        item="id"
        :show-select="showSelect"
        :loading="loading"
        @click:row="$emit('click:row', $event)"
      >
        <template v-slot:item.actions="{ item }" v-if="canEdit">
          <v-icon small class="mr-2" @click="$emit('editItem', item)"
            >mdi-pencil</v-icon
          >
        </template>
        <template
          v-slot:item="{ item, index }"
          v-if="!!this.$scopedSlots['item']"
        >
          <slot name="item" :item="item" :index="index"></slot>
        </template>
        <slot name="itemSlot"></slot>
        <template v-slot:item.name="{ item }">
          <slot name="itemName" v-bind:item="item"></slot>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { db } from '@/firebaseConfig'

export default {
  name: 'DataTable',
  props: {
    value: {
      type: Array,
      defualt: [],
    },
    title: String,
    headers: Array,
    dataPath: {
      type: String,
      required: true,
    },
    listenForChanges: {
      type: Boolean,
      default: true,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    defaultSortBy: {
      type: String,
    },
    defaultSortByDirection: {
      type: String,
      default: 'asc',
    },
    extraSortBy: {
      type: String,
    },
    extraSortByDirection: {
      type: String,
      default: 'asc',
    },
    search: {
      type: Array,
      validator: (prop) => prop.every((e) => e.length === 3),
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      totalItems: 0,
      snapshot: null,
      loading: false,
      options: {},
      listeners: [],
      page: 1,
      selectedItems: this.value,
    }
  },
  watch: {
    selectedItems: {
      handler(newVal) {
        this.$emit('input', newVal)
      },
      deep: true,
    },
    value: {
      handler(newVal) {
        this.selectedItems = newVal
      },
      deep: true,
    },
    // When the pagination/sorting options change, rerun loadData
    options: {
      handler() {
        this.loadData()
      },
      deep: true,
    },
    totalItems(newVal) {
      this.$emit('update:totalItems', newVal)
    },
    searchText(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadData()
      }
    },
  },
  mounted() {
    this.loadData()
  },
  beforeDestroy() {
    this.destroyOldListeners()
  },
  computed: {
    ...mapGetters(['companyId', 'searchText', 'hasSearchText', 'isMobile']),
    hasTableActions() {
      return !!this.$scopedSlots['actions']
    },
    path() {
      return this.dataPath
    },
    mergedHeaders() {
      return this.headers?.filter((h) => h.merge !== undefined) ?? undefined
    },
    items() {
      if (this.snapshot) {
        return this.snapshot.docs.map((doc) => {
          const item = {
            ...doc.data(),
          }

          Object.defineProperty(item, 'id', {
            value: doc.id,
          })

          if (this.mergedHeaders) {
            for (let mergedHeader of this.mergedHeaders) {
              const values = []
              mergedHeader.merge.values.forEach((valueKey) =>
                values.push(item[valueKey])
              )

              Object.defineProperty(item, mergedHeader.value, {
                value: values.join(mergedHeader.separator ?? ' '),
              })
            }
          }

          return item
        })
      }

      return []
    },
  },
  methods: {
    destroyOldListeners() {
      for (const idx in this.listeners) {
        this.listeners[idx]()
      }
      this.listeners = []
    },
    headerForValue(value) {
      return this.headers.filter((h) => h.value === value)[0]
    },
    applyOrderBy(
      dbRef,
      direction = this.defaultSortByDirection ?? 'asc',
      header = this.defaultSortBy ?? this.headers[0]
    ) {
      const merge = header.merge
      if (merge) {
        // Merge header
        merge.values.forEach((val) => {
          dbRef = dbRef.orderBy(val, direction)
        })
      } else {
        const val = typeof header === 'string' ? header : header.value
        dbRef = dbRef.orderBy(val, direction)
      }
      return dbRef
    },
    async loadData() {
      try {
        this.loading = true

        // The options provided by v-data-table for pagination
        const { sortBy, sortDesc, page, itemsPerPage } = this.options

        let dbRef = db.collection(this.path)

        if (this.search) {
          this.search.forEach((e) => {
            dbRef = dbRef.where(e[0], e[1], e[2])
          })
        }

        // Use search vuex module!
        // apply search if any
        if (this.hasSearchText) {
          dbRef = dbRef.where(
            'keywords',
            'array-contains',
            this.searchText.toLowerCase()
          )
        } else {
          // Apply extra sort by first
          if (this.extraSortBy && this.extraSortBy !== sortBy[0]) {
            dbRef = this.applyOrderBy(dbRef, this.extraSortByDirection, {
              value: this.extraSortBy,
            })
          }

          if (!sortBy[0]) {
            dbRef = this.applyOrderBy(dbRef)
          } else {
            const sortString = sortDesc[0] ? 'desc' : 'asc'
            const header = this.headerForValue(sortBy[0])
            dbRef = this.applyOrderBy(dbRef, sortString, header)
            // dbRef = dbRef.orderBy(header.value, sortString)
          }
        }

        if (page != 1 && this.snapshot) {
          if (page > this.page) {
            // Going forward
            const lastDoc = this.snapshot.docs[this.snapshot.docs.length - 1]
            dbRef = dbRef.startAfter(lastDoc)
          } else if (page < this.page) {
            // going backwards
            const firstDoc = this.snapshot.docs[0]
            dbRef = dbRef.endBefore(firstDoc)
          }
        }

        this.page = page

        if (itemsPerPage > 0) {
          dbRef = dbRef.limit(itemsPerPage)
        }

        if (this.listenForChanges) {
          this.destroyOldListeners()
          let listener = dbRef.onSnapshot(
            (snapshot) => {
              this.snapshot = snapshot
              this.loading = false
            },
            (error) => {
              console.error(error)
              this.loading = false
            }
          )

          this.listeners.push(listener)

          let countListener = db.doc('stats/' + this.path).onSnapshot(
            (snapshot) => {
              this.totalItems = snapshot.data()?.count ?? 0
            },
            (error) => {
              console.error(
                `DataTable count listener error (stats/${this.path}) : `,
                error.message
              )
            }
          )

          this.listeners.push(countListener)
        } else {
          try {
            this.snapshot = await dbRef.get()
            const documentCount = await db.doc('stats/' + this.path).get()
            this.totalItems = documentCount.data()?.count ?? 0
          } catch (error) {
            console.error(error)
          } finally {
            this.loading = false
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.data-table
  display: flex
  flex-grow: 1
.table-title
  @include sectionTitle
</style>
